import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';

/**
 * Computes pluck data from jobs.socData.
 *
 * @param {object} jobs - The jobs context (expects jobs.socData array).
 * @param {string} ratingField - Field to use for the rating (e.g., "totalOpeningsRating" or "growthRating").
 * @param {string} measureField - Field to use for the actual value (e.g., "avgTotalOpenings" or "longtermGrowth").
 * @param {number} fallbackRating - Default rating value if ratingField is null (default is 1).
 *
 * @returns {Array} Array of objects, each with a label and a range ([min, max]) for ratings 1–5.
 */
export function usePlucks(jobs, ratingField, measureField, fallbackRating = 1) {
  const [plucks, setPlucks] = useState([]);

  useEffect(() => {
    if (!jobs?.socData?.length) {
      setPlucks([]);
      return;
    }
    // Initialize extents for ratings 1 to 5.
    const rankExtents = {
      1: [Number.POSITIVE_INFINITY, 0],
      2: [Number.POSITIVE_INFINITY, 0],
      3: [Number.POSITIVE_INFINITY, 0],
      4: [Number.POSITIVE_INFINITY, 0],
      5: [Number.POSITIVE_INFINITY, 0],
    };

    jobs.socData.forEach((job) => {
      let r = job[ratingField];
      if (r === null || r === undefined) {
        r = fallbackRating;
      }
      const measure = job[measureField] ?? 0;
    // Skip if rating is out of [1..5] range:
      if (!rankExtents[r]) return;
      // Expand the min/max extents for this rating
      if (measure < rankExtents[r][0]) rankExtents[r][0] = measure;
      if (measure > rankExtents[r][1]) rankExtents[r][1] = measure;
    });

    const arr = [];
    for (let r = 1; r <= 5; r++) {
      let [mn, mx] = rankExtents[r];
      if (mn === Number.POSITIVE_INFINITY) mn = 0;
      if (mx < mn) mx = mn; // fallback if no data exists
      arr.push({
        label: `${mn}–${mx}`,
        range: [mn, mx],
      });
    }
    setPlucks(arr);
  }, [jobs, ratingField, measureField, fallbackRating]);

  return plucks;
}

/**
 * Returns a debounced function to update the advanced filter value based on a new range.
 *
 * @param {Array} plucks - The pluck data (array of 5 objects).
 * @param {string} filterKey - The key in the advancedFilters object (e.g., "openingsRange").
 * @param {function} setAdvancedFilters - Setter for the advancedFilters state.
 * @param {number} delay - Debounce delay in milliseconds (default 300ms).
 */
export function useDebouncedAdvancedFilterUpdate(plucks, filterKey, setAdvancedFilters, delay = 300) {
  const debouncedUpdate = useCallback(
    debounce((newRange) => {
      if (!plucks.length) return;
      const leftRange = plucks[newRange[0] - 1].range;
      const rightRange = plucks[newRange[1] - 1].range;
      setAdvancedFilters((prev) => ({
        ...prev,
        [filterKey]: [leftRange[0], rightRange[1]],
      }));
    }, delay),
    [plucks, setAdvancedFilters, filterKey, delay]
  );
  return debouncedUpdate;
}